// pages/ProductDetail.jsx
import React, { useState, useEffect, useLayoutEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import ProductHeroBit from '../components/product/ProductHeroBit';
import BenefitGridBit from '../components/product/BenefitGridBit';
import IngredientBit from '../components/product/IngredientBit';
import HowToUseBit from '../components/product/HowToUseBit';
import ResultsBit from '../components/product/ResultsBit';
import ReviewBit from '../components/product/ReviewBit';
import RelatedProductsBit from '../components/product/RelatedProductsBit';
import LuxuryLoading from '../components/ui/LuxuryLoading';
import '../styles/globals.css';
import { ProductService } from "../api/product.api";
import { CartService, cartEvents } from "../api/cart.api";
import { Reveal } from "../components/animations/Reveal";

const ProductDetail = () => {
    // Change from productId to productIdentifier to support both ID and slug
    const { productIdentifier } = useParams(); // Changed from productId
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(true);
    const [isScrolled, setIsScrolled] = useState(false);
    const [product, setProduct] = useState(null);
    const [error, setError] = useState(null);
    const [cartLoading, setCartLoading] = useState(false);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [cartMessage, setCartMessage] = useState(null);
    const [messageType, setMessageType] = useState(null);

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        fetchProductDetails();

        const onScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };

        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, [productIdentifier]); // Changed from productId

    useEffect(() => {
        if (cartMessage) {
            const timer = setTimeout(() => {
                setCartMessage(null);
                setMessageType(null);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [cartMessage]);

    useEffect(() => {
        const unsubscribe = cartEvents.subscribe('cartUpdated', () => {});

        return () => unsubscribe();
    }, []);

    const fetchProductDetails = async () => {
        try {
            setIsLoading(true);
            setError(null);

            // Use the generic getProductById which now handles both ID and slug
            const response = await ProductService.getProductById(productIdentifier);
            // console.log("API Response:", response);

            const { product, reviews, ratingBreakdown, totalReviews } = response;

            const transformedRatingBreakdown = ratingBreakdown || Object.entries(product.ratingBreakdown || {})
                .map(([stars, count]) => ({
                    stars: parseInt(stars),
                    percentage: totalReviews > 0 ? Math.round((count / totalReviews) * 100) : 0,
                    count: count
                }))
                .sort((a, b) => b.stars - a.stars);

            const formattedProduct = {
                ...product,
                slug: product.slug || productIdentifier, // Ensure slug is included
                reviews: reviews || [],
                ratingBreakdown: transformedRatingBreakdown,
                reviewCount: totalReviews || product.reviewCount || 0,
                rating: product.rating || 0,
                relatedProducts: [],

                variants: product.variants?.map(variant => ({
                    ...variant,
                    inStock: variant.stock?.canFulfill || false,
                    stock: variant.stock?.quantityAvailable || 0,
                    isLowStock: variant.stock?.isLowStock || false,
                    canFulfill: variant.stock?.canFulfill || false,
                    stockInfo: variant.stock
                })) || []
            };
            setProduct(formattedProduct);

            if (formattedProduct.variants && formattedProduct.variants.length > 0) {
                const inStockVariant = formattedProduct.variants.find(v => v.inStock);
                if (inStockVariant) {
                    setSelectedVariant(inStockVariant);
                } else {
                    setSelectedVariant(formattedProduct.variants[0]);
                }
            }
        } catch (err) {
            setError(err.message || 'Failed to load product');
            console.error('Error fetching product:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleVariantSelect = (variant) => {
        setSelectedVariant(variant);

        if (variant.stock < quantity) {
            setQuantity(Math.max(1, variant.stock));
        }
    };

    const handleQuantityChange = (newQuantity) => {
        if (!selectedVariant) return;

        const maxQuantity = selectedVariant.stock;
        const validQuantity = Math.min(Math.max(1, newQuantity), maxQuantity);
        setQuantity(validQuantity);
    };

    const showMessage = (message, type) => {
        setCartMessage(message);
        setMessageType(type);
    };

    const handleAddToCart = async () => {
        if (!product || !selectedVariant) {
            showMessage('Please select a variant', 'error');
            return;
        }

        if (!selectedVariant.inStock) {
            showMessage('This variant is currently out of stock', 'error');
            return;
        }

        if (quantity > selectedVariant.stock) {
            showMessage(`Only ${selectedVariant.stock} items available`, 'error');
            return;
        }

        try {
            setCartLoading(true);

            // Use product._id for API calls (cart operations still need the database ID)
            const result = await CartService.addToCart({
                productId: product._id || product.id, // Use actual ID for API
                variantId: selectedVariant.id,
                quantity: quantity
            });

            if (result.errors && result.errors.length > 0) {
                const error = result.errors[0];
                showMessage(error.error || 'Failed to add to cart', 'error');
            } else {
                showMessage(`Added ${quantity} ${quantity > 1 ? 'items' : 'item'} to cart!`, 'success');

                cartEvents.emit('itemAdded', {
                    productId: product._id || product.id,
                    variantId: selectedVariant.id,
                    productName: product.name,
                    quantity: quantity
                });

                setQuantity(1);
            }
        } catch (err) {
            console.error('Error adding to cart:', err);
            showMessage(err.message || 'Failed to add to cart', 'error');
        } finally {
            setCartLoading(false);
        }
    };

    const handleBuyNow = async () => {
        await handleAddToCart();

        if (messageType === 'success') {
            setTimeout(() => {
                navigate('/cart');
            }, 500);
        }
    };

    const getVariantStockText = (variant) => {
        if (!variant) return '';

        if (!variant.inStock) {
            return 'Out of Stock';
        }

        if (variant.isLowStock && variant.stock > 0) {
            return `Low Stock (${variant.stock} left)`;
        }

        if (variant.stock > 0) {
            return `In Stock (${variant.stock} available)`;
        }

        return 'Out of Stock';
    };

    const getVariantStockColor = (variant) => {
        if (!variant || !variant.inStock) {
            return 'text-charcoal/60';
        }

        if (variant.isLowStock) {
            return 'text-amber-600';
        }

        return 'text-sage';
    };

    const calculatePrices = () => {
        if (!product || !selectedVariant) {
            return {
                currentPrice: product?.price || 0,
                originalPrice: product?.originalPrice || product?.price || 0
            };
        }

        const variantPrice = selectedVariant.price;
        const discountPercent = product.discount || 0;

        let currentPrice = variantPrice;
        if (discountPercent > 0) {
            currentPrice = variantPrice * (1 - discountPercent / 100);
        }

        return {
            currentPrice,
            originalPrice: variantPrice
        };
    };

    const countOutOfStockVariants = () => {
        if (!product?.variants) return 0;
        return product.variants.filter(v => !v.inStock).length;
    };

    const calculateTotalAvailable = () => {
        if (!product?.variants) return 0;
        return product.variants.reduce((total, variant) => total + (variant.stock || 0), 0);
    };

    const isAnyVariantInStock = () => {
        if (!product?.variants) return false;
        return product.variants.some(v => v.inStock);
    };

    const isAnyVariantLowStock = () => {
        if (!product?.variants) return false;
        return product.variants.some(v => v.isLowStock && v.inStock);
    };

    if (error) {
        return (
            <div className="min-h-screen bg-pearl flex items-center justify-center">
                <div className="text-center">
                    <div className="text-2xl text-charcoal mb-4">Product Not Found</div>
                    <p className="text-charcoal/60 mb-4">
                        We couldn't find the product you're looking for.
                    </p>
                    <button
                        onClick={() => navigate('/shop')}
                        className="px-6 py-2 bg-champagne text-white rounded-full hover:bg-champagne/90 transition-colors"
                    >
                        Browse Products
                    </button>
                </div>
            </div>
        );
    }

    if (isLoading || !product) {
        return (
            <div className="min-h-screen bg-pearl">
                <Navbar />
                <div className="min-h-[80vh] flex items-center justify-center">
                    <LuxuryLoading size="large" />
                </div>
                <Footer />
            </div>
        );
    }

    const { currentPrice, originalPrice } = calculatePrices();
    const totalAvailable = calculateTotalAvailable();
    const outOfStockVariants = countOutOfStockVariants();
    const isInStock = isAnyVariantInStock();
    const isLowStock = isAnyVariantLowStock();

    // Get product image for metadata
    const productImage = product.images?.[0] || '';
    const productDescription = product.description || 'Premium skincare product';
    const productTitle = product.name ? `${product.name} | KROMAIC` : 'Product | KROMAIC';

    // Use slug for URLs, fallback to identifier
    const productSlug = product.slug || productIdentifier;
    const currentUrl = typeof window !== 'undefined' ?
        `${window.location.origin}/product/${productSlug}` :
        '';

    return (
        <div className="min-h-screen bg-pearl">
            {/* SEO Meta Tags with slug-based URL */}
            <Helmet>
                <title>{productTitle}</title>
                <meta name="description" content={productDescription} />
                <link rel="canonical" href={currentUrl} />

                {/* Open Graph Meta Tags */}
                <meta property="og:type" content="product" />
                <meta property="og:title" content={product.name || 'Luxury Product'} />
                <meta property="og:description" content={productDescription} />
                <meta property="og:image" content={productImage} />
                <meta property="og:url" content={currentUrl} />
                <meta property="product:price:amount" content={currentPrice.toString()} />
                <meta property="product:price:currency" content="USD" />
                {product?.category && <meta property="product:category" content={product.category} />}
                {product?.brand && <meta property="product:brand" content={product.brand} />}

                {/* Twitter Card Meta Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={product.name || 'Luxury Product'} />
                <meta name="twitter:description" content={productDescription} />
                <meta name="twitter:image" content={productImage} />

                {/* Additional Meta Tags */}
                <meta property="og:site_name" content="KROMAIC" />
                <meta name="robots" content="index, follow" />
            </Helmet>

            <div
                className={`sticky top-0 z-50 transition-all duration-300 ${
                    isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-soft' : 'bg-transparent'
                }`}
            >
                <Navbar />
            </div>

            {/* Cart Message Notification */}
            {cartMessage && (
                <div className={`fixed top-20 left-1/2 transform -translate-x-1/2 z-50 px-6 py-3 rounded-lg shadow-lg transition-all duration-300 ${
                    messageType === 'success'
                        ? 'bg-sage/10 border border-sage/30 text-sage'
                        : 'bg-red-50 border border-red-200 text-red-600'
                }`}>
                    <div className="flex items-center gap-2">
                        <span className="text-lg">
                            {messageType === 'success' ? '✓' : '✗'}
                        </span>
                        <span>{cartMessage}</span>
                    </div>
                </div>
            )}

            <AnimatePresence mode="wait">
                <motion.div
                    key="content"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                >
                    {/* HERO */}
                    <section id="product-hero">
                        <ProductHeroBit
                            product={product}
                            onAddToCart={handleAddToCart}
                            isLoading={cartLoading}
                            selectedVariant={selectedVariant}
                            onVariantSelect={handleVariantSelect}
                            quantity={quantity}
                            onQuantityChange={handleQuantityChange}
                            onBuyNow={handleBuyNow}
                            currentPrice={currentPrice}
                            originalPrice={originalPrice}
                            variantStockText={getVariantStockText(selectedVariant)}
                            variantStockColor={getVariantStockColor(selectedVariant)}
                        />
                    </section>

                    {/* REVIEWS */}
                    <section id="reviews" className="section-spacing">
                        <div className="container-luxury">
                            <ReviewBit
                                rating={product.rating}
                                reviewCount={product.reviewCount}
                                reviews={product.reviews}
                                ratingBreakdown={product.ratingBreakdown}
                                productId={product._id || product.id} // Pass actual ID for API calls
                                productSlug={productSlug} // Pass slug for display
                            />
                        </div>
                    </section>

                    {/* BENEFITS */}
                    <section id="benefits" className="section-spacing">
                        <div className="container-luxury">
                            <Reveal>
                                <BenefitGridBit benefits={product.benefits || []} />
                            </Reveal>
                        </div>
                    </section>

                    {/* INGREDIENTS */}
                    <section id="ingredients" className="section-spacing bg-nude/20">
                        <div className="container-luxury">
                            <IngredientBit
                                ingredients={product.ingredients || []}
                                freeFrom={product.freeFrom || []}
                            />
                        </div>
                    </section>

                    {/* HOW TO USE */}
                    <section id="how-to-use" className="section-spacing">
                        <div className="container-luxury">
                            <HowToUseBit ritualSteps={product.ritualSteps || []} />
                        </div>
                    </section>

                    {/* RESULTS */}
                    <section id="results" className="section-spacing bg-gradient-to-b from-pearl to-nude/10">
                        <div className="container-luxury">
                            <ResultsBit
                                concerns={product.concerns || []}
                                skinTypes={product.skinTypes || []}
                            />
                        </div>
                    </section>

                    {/* RELATED */}
                    <section id="related" className="section-spacing bg-nude/10">
                        <div className="container-luxury">
                            <RelatedProductsBit
                                relatedProducts={product.relatedProducts || []}
                                currentProductSlug={productSlug}
                                currentProductId={product._id || product.id}
                            />
                        </div>
                    </section>
                </motion.div>
            </AnimatePresence>

            {/* Sticky Add to Cart Bar for Mobile */}
            {product && (
                <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-nude/30 shadow-lg z-40 p-4">
                    <div className="container-luxury">
                        <div className="flex items-center justify-between gap-4">
                            <div className="flex flex-col min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="text-lg font-semibold text-charcoal">
                                        ${currentPrice.toFixed(2)}
                                    </span>
                                    {product.discount > 0 && (
                                        <span className="text-sm text-charcoal/60 line-through">
                                            ${originalPrice.toFixed(2)}
                                        </span>
                                    )}
                                </div>
                                <div className="truncate">
                                    <span className="text-xs text-charcoal/40">
                                        {selectedVariant?.name || 'Select variant'}
                                    </span>
                                    {selectedVariant && (
                                        <span className={`text-xs ml-2 font-medium ${getVariantStockColor(selectedVariant)}`}>
                                            {getVariantStockText(selectedVariant)}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <button
                                onClick={handleAddToCart}
                                disabled={!selectedVariant?.inStock || cartLoading}
                                className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors min-w-[120px] ${
                                    !selectedVariant?.inStock
                                        ? 'bg-charcoal/10 text-charcoal/40 cursor-not-allowed'
                                        : 'bg-champagne text-charcoal hover:bg-champagne/90'
                                }`}
                            >
                                {cartLoading ? 'Adding...' :
                                    !selectedVariant?.inStock ? 'Out of Stock' :
                                        'Add to Cart'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default ProductDetail;